import * as React from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { cn } from "@/lib/utils";

// ─────────────────────────────────────────────
// Textarea (with character count)
//
// Multi-line text input with optional label,
// character count, and error state.
// Design: §6.5 Form Controls
// ─────────────────────────────────────────────

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  hasError?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, hasError, ...props }, ref) => {
    return (
      <textarea
        ref={ref}
        className={cn(
          "min-h-[80px] w-full rounded-control border bg-bg-input px-3 py-2",
          "text-[14px] text-text-primary placeholder:text-text-muted",
          "resize-y transition-colors focus:outline-none focus:ring-1",
          hasError
            ? "border-danger focus:border-danger focus:ring-danger"
            : "border-border-default focus:border-accent focus:ring-accent",
          className
        )}
        {...props}
      />
    );
  }
);
Textarea.displayName = "Textarea";

// ── TextareaGroup (label + textarea + char count + error) ─
interface TextareaGroupProps extends TextareaProps {
  label?: string;
  helperText?: string;
  errorText?: string;
  required?: boolean;
  maxLength?: number;
  showCharCount?: boolean;
}

const TextareaGroup = React.forwardRef<HTMLTextAreaElement, TextareaGroupProps>(
  (
    {
      label,
      helperText,
      errorText,
      required,
      id,
      className,
      maxLength,
      showCharCount = true,
      value,
      defaultValue,
      onChange,
      ...textareaProps
    },
    ref
  ) => {
    const [charCount, setCharCount] = React.useState(
      () => String(value ?? defaultValue ?? "").length
    );
    const textareaId = id ?? label?.toLowerCase().replace(/\s+/g, "-");
    const hasError = Boolean(errorText);

    function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
      setCharCount(e.target.value.length);
      onChange?.(e);
    }

    return (
      <div className={cn("flex flex-col gap-[6px]", className)}>
        {label && (
          <LabelPrimitive.Root
            htmlFor={textareaId}
            className="text-[14px] font-medium text-text-secondary"
          >
            {label}
            {required && <span className="ml-1 text-danger">*</span>}
          </LabelPrimitive.Root>
        )}

        <Textarea
          ref={ref}
          id={textareaId}
          hasError={hasError}
          maxLength={maxLength}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          aria-describedby={
            errorText ? `${textareaId}-error` : helperText ? `${textareaId}-helper` : undefined
          }
          aria-invalid={hasError}
          {...textareaProps}
        />

        <div className="flex items-start justify-between gap-2">
          <div>
            {errorText && (
              <p id={`${textareaId}-error`} className="text-[12px] text-danger">
                {errorText}
              </p>
            )}
            {!errorText && helperText && (
              <p id={`${textareaId}-helper`} className="text-caption text-text-muted">
                {helperText}
              </p>
            )}
          </div>
          {showCharCount && maxLength && (
            <p
              className={cn(
                "flex-shrink-0 text-caption",
                charCount >= maxLength ? "text-danger" : "text-text-muted"
              )}
            >
              {charCount}/{maxLength}
            </p>
          )}
        </div>
      </div>
    );
  }
);
TextareaGroup.displayName = "TextareaGroup";

export { Textarea, TextareaGroup };
